import type { TimelineEventViewModel } from "../domain/types";
import { t } from "../i18n";
import { formatRelativeTime } from "../lib/time";
import { ArrowDownLeft, ArrowUpRight, UserMinus, UserPlus } from "lucide-react";

interface TimelineEventCardProps {
  event: TimelineEventViewModel;
  onClick?: () => void;
}

const typeConfig: Record<string, { icon: typeof UserPlus; tone: string; labelKey: string }> = {
  started_following: {
    icon: UserPlus,
    tone: "success",
    labelKey: "timelineStartedFollowing",
  },
  stopped_following: {
    icon: UserMinus,
    tone: "danger",
    labelKey: "timelineStoppedFollowing",
  },
  gained_follower: {
    icon: ArrowDownLeft,
    tone: "info",
    labelKey: "timelineGainedFollower",
  },
  lost_follower: {
    icon: ArrowUpRight,
    tone: "warning",
    labelKey: "timelineLostFollower",
  },
};

const confidenceLabels: Record<string, string | null> = {
  confirmed: null,
  pending_confirmation: "timelinePendingConfirmation",
  low_confidence: "timelineLowConfidence",
  discarded: "timelineDiscarded",
};

export function TimelineEventCard({ event, onClick }: TimelineEventCardProps) {
  const config = typeConfig[event.type] ?? typeConfig.started_following;
  const Icon = config.icon;
  const confidenceLabel = confidenceLabels[event.confidence];

  return (
    <div 
      className={`app-event-card ${event.confidence === "discarded" ? "is-discarded" : ""} ${onClick ? "is-clickable" : ""}`}
      onClick={onClick}
    >
      <div className="app-event-avatar">
        {event.targetProfile.avatarUrl ? (
          <img src={event.targetProfile.avatarUrl} alt="" />
        ) : (
          event.targetProfile.displayName?.charAt(0) ?? event.targetProfile.handle.charAt(0).toUpperCase()
        )}
      </div>

      <div className="app-event-content">
        <div className="app-event-profile">
          <strong>@{event.targetProfile.handle}</strong>
          {event.targetProfile.isVerified && <span className="app-verified-badge">✓</span>}
          {event.targetProfile.displayName && <span>{event.targetProfile.displayName}</span>}
          {event.targetProfile.isMutual && <em>{t("mutualBadge")}</em>}
        </div>

        <p className={`app-event-action app-event-action-${config.tone}`}>
          <Icon size={17} />
          {t(config.labelKey)}
          <span>·</span>
          {formatRelativeTime(event.occurredAt)}
          {event.sourceProfile.handle && <small>@{event.sourceProfile.handle}</small>}
          {confidenceLabel && <em>{t(confidenceLabel)}</em>}
        </p>
      </div>
    </div>
  );
}
